import { useState } from 'react';

import { toastService } from '@thefarmersfront/kitchen';

import { useAPI, useAsyncHandler } from '~/hooks/common';
import { addIf } from '~/utils';

import { API_ENDPOINT, STOCK_KEYWORD_TYPE_FILTER_KEY, TOAST_MESSAGE } from '~/constants';

import { TypeErrorResponseData } from '~/types/common.types';
import {
  TypeGetVendorReleaseInventoryParams,
  TypeGetVendorReleaseInventoryResponse,
  TypeStockReleaseInventoryFilter,
} from '~/types/stock.types';

const useGetVendorReleaseInventory = () => {
  const { runAsync } = useAsyncHandler();

  const [responseData, setResponseData] = useState<TypeGetVendorReleaseInventoryResponse | null>(null);

  const postVendorReleaseInventory = useAPI<TypeGetVendorReleaseInventoryParams, TypeGetVendorReleaseInventoryResponse>(
    {
      endpoint: API_ENDPOINT.STOCK.POST_VENDOR_RELEASE_INVENTORY,
      method: 'post',
    }
  );

  const requestVendorReleaseInventory = (
    filterState: TypeStockReleaseInventoryFilter,
    {
      onSuccess,
      onError,
    }: {
      onSuccess?: (data: TypeGetVendorReleaseInventoryResponse) => void;
      onError?: (error: TypeErrorResponseData['error']) => void;
    } = {}
  ) => {
    const { cluster, keywordType, keywordValue, pageSize, currentPage } = filterState;
    const keywordFilterKey =
      STOCK_KEYWORD_TYPE_FILTER_KEY[keywordType?.value as keyof typeof STOCK_KEYWORD_TYPE_FILTER_KEY];

    if (!cluster?.value) {
      return;
    }

    const params: TypeGetVendorReleaseInventoryParams = {
      cluster: cluster.value,
      ...addIf(keywordFilterKey && keywordValue, keywordFilterKey, keywordValue),
      cursor: currentPage,
      limit: pageSize,
    };

    runAsync(() => postVendorReleaseInventory({ params }), {
      onSuccess: (res) => {
        setResponseData(res);
        onSuccess?.(res);
      },
      onError: (error: TypeErrorResponseData['error']) => {
        toastService.addToast({
          type: 'error',
          message: error.message ?? TOAST_MESSAGE.DEFAULT_ERROR,
        });
        onError?.(error);
      },
    });
  };

  const resetResponseData = () => {
    setResponseData(null);
  };

  return { requestVendorReleaseInventory, responseData, resetResponseData };
};

export default useGetVendorReleaseInventory;
